import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, Typography,
  Box, Pagination, Skeleton, Alert, Stack, TextField, Chip, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Standard list surface: search, filter chips, result count, table,
 * pagination. Handles loading, empty and error states so individual
 * pages don't each reinvent them.
 */
export default function DataTable({
  columns,
  rows,
  total,
  loading,
  error,
  page,
  pageSize,
  pageCount,
  search,
  activeFilter,
  filterOptions = [],
  onSearchChange,
  onFilterChange,
  onPageChange,
  searchPlaceholder = 'Search…',
  emptyMessage = 'Nothing here yet.',
  getRowKey = (r) => r.id,
}) {
  const searching = !!search || !!activeFilter;

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          sx={{ minWidth: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        {filterOptions.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filterOptions.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                color={activeFilter === f.key ? 'primary' : 'default'}
                variant={activeFilter === f.key ? 'filled' : 'outlined'}
                onClick={() => onFilterChange(f.key)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {!loading && !error && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {total === 0
            ? 'No results'
            : `${total.toLocaleString()} ${total === 1 ? 'result' : 'results'}`}
          {searching && total > 0 && ' (filtered)'}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align}>{c.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: Math.min(pageSize, 8) }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {columns.map((c) => (
                  <TableCell key={c.key}><Skeleton variant="text" /></TableCell>
                ))}
              </TableRow>
            ))}

            {!loading && rows.map((row) => (
              <TableRow key={getRowKey(row)} hover>
                {columns.map((c) => (
                  <TableCell key={c.key} align={c.align}>
                    {c.render ? c.render(row) : row[c.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {searching
                      ? 'No records match your search or filter.'
                      : emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, p) => onPageChange(p)}
            color="primary"
            siblingCount={1}
          />
        </Box>
      )}
    </>
  );
}